import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FileText, Download, Trash2, RotateCcw, XCircle, Search, Filter,
  ExternalLink, ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle, Clock,
  Check, ArrowRight
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../../auth/AuthContext";
import {
  fetchReportJobs, cancelReportJob, downloadReportPdf, deleteReportJob, createReportJob, previewReportHtml
} from "../../api/reports";
import type { ReportJob } from "../../api/reports";
import "../../styles/api-access.css";

export default function ReportsHubPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const isPaidEligible = useMemo(() => {
    if (!user) return false;
    const role = (user.role || "").toLowerCase();
    if (role === "admin" || role === "analyst" || role === "super_admin") {
      return true;
    }
    const plan = (user.plan || "").toLowerCase().trim();
    if (plan === "business pro" || plan === "business_pro" || plan === "pro" || plan === "enterprise") {
      return true;
    }
    return false;
  }, [user]);

  const [jobs, setJobs] = useState<ReportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<"mine" | "everyone">("mine");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [remainingReports, setRemainingReports] = useState<number>(5);
  const [dailyLimit, setDailyLimit] = useState<number>(5);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const isAdminOrAnalyst = user?.role === "admin" || user?.role === "analyst";

  useEffect(() => {
    if (!isPaidEligible) {
      setLoading(false);
      return;
    }
    localStorage.setItem("has_visited_reports", "true");
    loadJobs();
  }, [scope, isPaidEligible]);

  // Polling for active jobs
  useEffect(() => {
    if (!isPaidEligible) return;
    const hasActive = jobs.some(j => j.status === "queued" || j.status === "running");
    if (!hasActive) return;

    const interval = setInterval(() => {
      loadJobs(true);
    }, 2500);

    return () => clearInterval(interval);
  }, [jobs, scope, isPaidEligible]);

  const loadJobs = async (silent = false) => {
    if (!isPaidEligible) return;
    if (!silent) setLoading(true);
    try {
      const data = await fetchReportJobs(scope);
      setJobs(data.jobs || []);
      setRemainingReports(data.remaining_reports_today);
      setDailyLimit(data.daily_limit);
    } catch (err) {
      if (!silent) toast.error("Failed to load reports");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleDownload = async (job: ReportJob) => {
    setDownloadingId(job._id);
    try {
      const filename = `threatactix_report_${job.domain}_${job.period}.pdf`;
      await downloadReportPdf(job._id, filename);
      toast.success("Report downloaded successfully");
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Failed to download PDF report";
      toast.error(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCancel = async (jobId: string) => {
    try {
      await cancelReportJob(jobId);
      toast.success("Job cancellation requested");
      loadJobs(true);
    } catch (err) {
      toast.error("Failed to cancel job");
    }
  };

  const handleDelete = async (jobId: string) => {
    try {
      await deleteReportJob(jobId);
      setJobs(prev => prev.filter(j => j._id !== jobId));
      toast.success("Report deleted");
    } catch (err) {
      toast.error("Failed to delete report");
    }
  };

  const handleRegenerate = async (job: ReportJob) => {
    try {
      await createReportJob(job.domain, job.sector, job.period, job.format);
      toast.success(`Regenerating report for ${job.domain}`);
      loadJobs();
    } catch (err: any) {
      const msg = err.response?.data?.detail?.message || err.response?.data?.message || "Failed to regenerate report";
      toast.error(msg);
    }
  };

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const matchSearch = !searchQuery || job.domain.toLowerCase().includes(searchQuery.toLowerCase()) || job.company_name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchStatus = statusFilter === "all" || job.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [jobs, searchQuery, statusFilter]);

  const formatBytes = (bytes: number) => {
    if (!bytes || bytes === 0) return "-";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  if (authLoading) {
    return (
      <div className="reports-hub-page leaks-page p-6 max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw size={28} className="animate-spin text-indigo-500" />
          <span className="text-sm text-slate-400">Verifying access entitlements...</span>
        </div>
      </div>
    );
  }

  // Restriction View matching Image 2 for Free / Ineligible tier
  if (!isPaidEligible) {
    return (
      <div className="w-full px-6 py-6 sm:px-8">
        {/* Large Page Title exact same as Image 2 */}
        <h1 className="tx-exclusive-page-title">
          Exclusive reports
        </h1>

        {/* Gold Restriction Banner matching Image 2 */}
        <div className="tx-api-restriction-banner" style={{ maxWidth: "1000px" }}>
          <div className="tx-api-restriction-banner-text">
            <span>Please upgrade your plan to access this feature</span>
          </div>
          <button
            type="button"
            className="tx-api-restriction-banner-btn"
            style={{ fontWeight: 500 }}
            onClick={() => navigate("/subscription")}
          >
            <div className="flex justify-center items-center">
              <img
                alt=""
                className="w-[14px] h-[14px] my-auto mr-[4px]"
                src="https://d1898qjf7hzy9p.cloudfront.net/icons/premium-diamond.svg"
              />
              <span>Upgrade</span>
            </div>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-hub-page leaks-page p-6 max-w-7xl mx-auto">
      {/* BREADCRUMB */}
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
        <span>REPORTS</span> / <span className="text-slate-200">OVERVIEW</span>
      </div>

      {/* HEADER ROW */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
            <FileText className="text-indigo-500" size={30} />
            Exclusive Threat Reports
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Access and download generated intelligence reports, domain exposure statistics, and sector landscape PDFs.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="px-3.5 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-xs font-semibold text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
            <Clock size={14} />
            <span>{remainingReports} of {dailyLimit} daily reports remaining</span>
          </div>

          <button
            onClick={() => navigate("/domain-monitor")}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-sm rounded-lg transition-colors shadow-sm"
          >
            + New Report
          </button>
        </div>
      </div>

      {/* CONTROLS BAR */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 mb-6 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-3 w-full md:w-auto flex-1">
          {/* Search Box */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search reports by domain or company..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 outline-none focus:border-indigo-500 transition-colors"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white">
                <XCircle size={16} />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter size={15} className="text-slate-400" />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-indigo-500"
            >
              <option value="all">All Statuses</option>
              <option value="done">Completed</option>
              <option value="running">Processing</option>
              <option value="queued">Queued</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Scope Switch for Admin/Analyst */}
        {isAdminOrAnalyst && (
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700 text-xs font-semibold">
            <button
              onClick={() => setScope("mine")}
              className={`px-3 py-1.5 rounded-md transition-colors ${scope === "mine" ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}
            >
              My Reports
            </button>
            <button
              onClick={() => setScope("everyone")}
              className={`px-3 py-1.5 rounded-md transition-colors ${scope === "everyone" ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}
            >
              Everyone's Reports
            </button>
          </div>
        )}
      </div>

      {/* TABLE CONTENT */}
      {loading ? (
        <div className="space-y-3 mt-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-slate-100 dark:bg-slate-800/50 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-slate-300 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900/50">
          <FileText className="mx-auto text-slate-400 mb-3" size={40} />
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">No threat reports found</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto mb-6">
            {searchQuery ? "No reports matched your search criteria." : "You haven't generated any domain exposure reports yet."}
          </p>
          <button
            onClick={() => navigate("/domain-monitor")}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-sm rounded-lg transition-colors"
          >
            Go to Domain Monitor
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="py-3.5 px-4">Domain / Company</th>
                  <th className="py-3.5 px-4">Sector</th>
                  <th className="py-3.5 px-4">Period</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4">Size</th>
                  <th className="py-3.5 px-4">Created</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 text-slate-700 dark:text-slate-300">
                {filteredJobs.map(job => {
                  const isDone = job.status === "done";
                  const isRunning = job.status === "running" || job.status === "queued";
                  const isFailed = job.status === "failed";

                  return (
                    <tr key={job._id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-slate-900 dark:text-white">{job.domain}</div>
                        <div className="text-xs text-slate-400">{job.company_name}</div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                          {job.sector}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-xs text-slate-500 dark:text-slate-400">{job.period}</td>
                      <td className="py-3.5 px-4">
                        {isDone && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                            <CheckCircle2 size={13} /> Completed
                          </span>
                        )}
                        {isRunning && (
                          <div className="flex flex-col gap-1 max-w-[140px]">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                              <RefreshCw size={13} className="animate-spin" /> {job.step}
                            </span>
                            <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                              <div className="bg-indigo-500 h-full transition-all duration-300" style={{ width: `${job.progress}%` }} />
                            </div>
                          </div>
                        )}
                        {isFailed && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20">
                            <AlertTriangle size={13} /> Failed
                          </span>
                        )}
                        {job.status === "cancelled" && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-500/10 text-slate-500 dark:text-slate-400 border border-slate-500/20">
                            <XCircle size={13} /> Cancelled
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-xs font-mono text-slate-500 dark:text-slate-400">{formatBytes(job.file_size)}</td>
                      <td className="py-3.5 px-4 text-xs text-slate-500 dark:text-slate-400">
                        {job.created_at ? new Date(job.created_at).toLocaleDateString() : "-"}
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isDone && (
                            <>
                              {job.format === 'pdf_v2' && (
                                <button
                                  onClick={() => previewReportHtml(job._id)}
                                  className="px-3 py-1.5 bg-slate-50 dark:bg-slate-500/10 hover:bg-slate-100 dark:hover:bg-slate-500/20 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-500/30 rounded-lg text-xs font-medium transition-colors inline-flex items-center gap-1.5"
                                >
                                  <ExternalLink size={14} /> Preview
                                </button>
                              )}
                              <button
                                onClick={() => handleDownload(job)}
                                disabled={downloadingId === job._id}
                                className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/30 rounded-lg text-xs font-medium transition-colors inline-flex items-center gap-1.5"
                              >
                                <Download size={14} /> Download PDF
                              </button>
                            </>
                          )}

                          {isRunning && (
                            <button
                              onClick={() => handleCancel(job._id)}
                              className="px-3 py-1.5 bg-rose-50 dark:bg-rose-500/10 hover:bg-rose-100 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30 rounded-lg text-xs font-medium transition-colors"
                            >
                              Cancel
                            </button>
                          )}

                          {isFailed && (
                            <button
                              onClick={() => handleRegenerate(job)}
                              className="px-3 py-1.5 bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30 rounded-lg text-xs font-medium transition-colors inline-flex items-center gap-1.5"
                            >
                              <RotateCcw size={14} /> Retry
                            </button>
                          )}

                          <button
                            onClick={() => handleDelete(job._id)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                            title="Delete report"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
